/**
 * Shared restaurant thermal receipt HTML (invoice / KOT).
 * Layout must stay in sync with billing print — do not diverge styles here.
 */
import {
  getThermalPaperWidthMm,
  hasDesktopPrintApi,
  isPrinterValidated,
  openPrinterValidationDialog,
  silentPrintRestaurantHtml,
} from '../../../utils/desktopPrinter';

export const escapeReceiptText = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

export const formatReceiptAmount = (value) => Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatReceiptOrderNumber = (orderNumber) => (
  String(orderNumber || '').replace(/^POS-/i, '') || '--'
);

const normalizeQueueTimers = (timers = []) => (
  Array.isArray(timers) ? timers.filter(Boolean) : []
);

/**
 * Normalize order line items into receipt rows used by the thermal HTML.
 */
export function getPrintableReceiptItems(order, {
  menuItems = [],
  menuVariations = [],
  fallbackItems = [],
} = {}) {
  const menuLookup = new Map((menuItems || []).map((item) => [Number(item.id), item]));
  const variationLookup = new Map(
    (menuVariations || []).map((variation) => [Number(variation.id), variation])
  );
  const sourceItems = Array.isArray(order?.items) && order.items.length
    ? order.items
    : (Array.isArray(order?.order_items) && order.order_items.length
      ? order.order_items
      : fallbackItems);

  return (Array.isArray(sourceItems) ? sourceItems : [])
    .filter((item) => Number(item?.quantity || 0) > 0)
    .map((item) => {
      const menuItem = menuLookup.get(Number(item.menu_item || item.menu_item_id));
      const variationId = item.variation_id != null && item.variation_id !== ''
        ? Number(item.variation_id)
        : null;
      const variation = variationId ? variationLookup.get(Number(variationId)) : null;
      const baseName = item.menu_item_name || item.item_name || item.name || menuItem?.name || 'Item';
      const variationName = item.variation_label || item.variation_name || variation?.name || '';
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(
        item.unit_price
        ?? item.price
        ?? item.rate
        ?? (
          Number(menuItem?.price || 0)
          + (variationId ? Number(variation?.price || item.variation_price || 0) : 0)
        )
      );
      const amount = Number(item.amount ?? item.total ?? (unitPrice * quantity));

      return {
        name: variationName ? `${baseName} (${variationName})` : baseName,
        quantity,
        unitPrice,
        amount,
        note: item.special_note || item.note || '',
      };
    });
}

function resolveHotelContext(hotelData = {}) {
  const djangoContext = typeof window !== 'undefined' ? (window.DJANGO_CONTEXT || {}) : {};
  const selectedHotel = djangoContext.selectedHotel || {};
  const hotelName = hotelData?.name
    || hotelData?.selected_hotel?.name
    || selectedHotel?.name
    || 'ROOMNEXA HOTEL & RESTAURANT';
  const hotelLogoUrl = selectedHotel?.logo_url
    || selectedHotel?.image_url
    || selectedHotel?.logo
    || hotelData?.logo_url
    || '';
  const hotelAddress = [
    selectedHotel?.address || hotelData?.address,
    selectedHotel?.city || hotelData?.city,
    selectedHotel?.state || hotelData?.state,
    selectedHotel?.country || hotelData?.country,
  ].filter(Boolean).join(', ');
  const hotelGstin = selectedHotel?.gstin
    || selectedHotel?.gst_number
    || selectedHotel?.gst
    || hotelData?.gstin
    || '';
  const cashierFallback = djangoContext?.user?.username || 'biller';
  return {
    hotelName,
    hotelLogoUrl,
    hotelAddress,
    hotelGstin,
    cashierFallback,
  };
}

/**
 * Build thermal receipt HTML identical to RestaurantLandingPage billing print.
 */
export function buildRestaurantReceiptHtml(order, printType = 'bill', printOptions = {}, extras = {}) {
  const widthMm = Number(printOptions.paperWidthMm || extras.widthMm || getThermalPaperWidthMm() || 80);
  const orderItems = Array.isArray(printOptions.orderItems)
    ? printOptions.orderItems
    : getPrintableReceiptItems(order, {
      menuItems: extras.menuItems,
      menuVariations: extras.menuVariations,
      fallbackItems: extras.fallbackItems,
    });
  const now = printOptions.now instanceof Date ? printOptions.now : new Date();
  const orderNumber = formatReceiptOrderNumber(order?.order_number || order?.id || '');
  const hotel = resolveHotelContext(extras.hotel || extras.hotelData || {});
  const cashierName = order?.cashier_name
    || order?.created_by_name
    || extras.cashierName
    || hotel.cashierFallback;
  const targetLabel = extras.targetLabel
    || order?.room_number
    || order?.table_name
    || '';
  const orderTypeLabel = extras.orderTypeLabel
    || (order?.order_type === 'room_service'
      ? 'Room Service'
      : order?.order_type === 'takeaway'
        ? 'Takeaway'
        : order?.order_type === 'dine_in'
          ? 'Dine In'
          : 'Order');
  const customerName = order?.customer_name || extras.customerName || '';
  const customerPhone = order?.customer_phone || extras.customerPhone || extras.mobile || '';
  const title = printType === 'kot' ? 'KOT' : 'INVOICE';
  const isKotPrint = printType === 'kot';
  const showKotPreviewToolbar = isKotPrint && printOptions.showPreviewToolbar !== false;
  const autoPrintOnLoad = printOptions.autoPrintOnLoad ?? !isKotPrint;
  const queueTimers = normalizeQueueTimers(order?.queue_timers);
  const kotNumber = Number(printOptions.kotNumber || Math.max(1, queueTimers.length || 1));
  const isRunningKot = isKotPrint && (kotNumber > 1 || order?.is_running_order);
  const subtotal = Number(
    order?.subtotal ?? order?.sub_total ?? extras.subtotal ?? 0
  );
  const receiptDiscountAmount = Number(
    order?.discount_amount ?? extras.discountAmount ?? 0
  );
  const deliveryCharge = Number(
    order?.delivery_charge ?? extras.deliveryCharge ?? 0
  );
  const containerCharge = Number(
    order?.container_charge ?? extras.containerCharge ?? 0
  );
  const grandTotal = Number(
    order?.total_amount ?? extras.grandTotal ?? extras.estimatedTotal ?? subtotal
  );
  const roundedGrandTotal = Math.round(grandTotal);
  const roundOffAmount = Number((roundedGrandTotal - grandTotal).toFixed(2));
  const displayGrandTotal = Math.abs(roundOffAmount) > 0.004 ? roundedGrandTotal : grandTotal;
  const taxRows = Array.isArray(order?.tax_breakdown)
    ? order.tax_breakdown
    : (Array.isArray(extras.taxBreakdown) ? extras.taxBreakdown : []);
  const taxLines = (Array.isArray(taxRows) ? taxRows : [])
    .filter((row) => row?.enabled !== false && Number(row?.amount || 0) > 0)
    .map((row) => `
        <div class="receipt-row">
          <span>${escapeReceiptText(row.tax_type_label || row.name || 'Tax')}</span>
          <span>${formatReceiptAmount(row.amount)}</span>
        </div>
      `)
    .join('');
  const hasEnabledGst = taxLines.trim().length > 0;
  const kotNotes = String(order?.notes || extras.notes || '').trim();
  const desktopShell = hasDesktopPrintApi();

  const billItemRows = orderItems.map((item) => `
      <tr>
        <td>
          <div>${escapeReceiptText(item.name)}</div>
          ${item.note ? `<div class="muted">Note: ${escapeReceiptText(item.note)}</div>` : ''}
        </td>
        <td class="num">${formatReceiptAmount(item.quantity).replace('.00', '')}</td>
        <td class="num">${formatReceiptAmount(item.unitPrice)}</td>
        <td class="num">${formatReceiptAmount(item.amount)}</td>
      </tr>
    `).join('');
  const kotItemRows = orderItems.map((item) => `
      <tr>
        <td class="kot-item">${escapeReceiptText(item.name)}</td>
        <td class="kot-qty">${formatReceiptAmount(item.quantity).replace('.00', '')}</td>
      </tr>
      ${item.note ? `<tr><td colspan="2" class="muted">Note: ${escapeReceiptText(item.note)}</td></tr>` : ''}
    `).join('');

  return `<!doctype html>
      <html>
        <head>
          <title>${escapeReceiptText(title)} ${escapeReceiptText(orderNumber)}</title>
          <meta charset="utf-8" />
          <style>
            @page { size: ${widthMm}mm auto; margin: 0; }
            * { box-sizing: border-box; }
            body {
              width: ${widthMm}mm;
              max-width: ${widthMm}mm;
              margin: 0;
              padding: 1mm;
              color: #000;
              background: #fff;
              font-family: "Courier New", monospace;
              font-size: ${widthMm === 58 ? '10px' : '11px'};
              line-height: 1.25;
            }
            .logo {
              display: block;
              max-width: ${widthMm === 58 ? '28mm' : '36mm'};
              max-height: 18mm;
              object-fit: contain;
              margin: 0 auto 2px;
              filter: grayscale(1) contrast(1.25);
            }
            .center { text-align: center; }
            .bold { font-weight: 700; }
            .title { font-size: ${widthMm === 58 ? '13px' : '15px'}; font-weight: 800; text-transform: uppercase; }
            .sub-title { font-size: ${widthMm === 58 ? '11px' : '12px'}; font-weight: 700; margin-top: 2px; }
            .line { border-top: 1px dashed #000; margin: 5px 0; }
            .receipt-row { display: flex; justify-content: space-between; gap: 8px; }
            .receipt-row span:first-child { max-width: 66%; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 2px 0; vertical-align: top; }
            th { border-bottom: 1px dashed #000; text-align: left; }
            .num { text-align: right; white-space: nowrap; }
            .muted { font-size: 0.9em; }
            .grand { font-size: ${widthMm === 58 ? '13px' : '15px'}; font-weight: 800; }
            .name-line { border-bottom: 1px solid #000; height: 14px; margin-top: 2px; }
            .meta-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 6px; row-gap: 1px; }
            .kot-time { font-size: ${widthMm === 58 ? '18px' : '20px'}; }
            .kot-no { font-size: ${widthMm === 58 ? '20px' : '23px'}; font-weight: 800; }
            .kot-type { font-size: ${widthMm === 58 ? '23px' : '26px'}; font-weight: 900; }
            .kot-target { font-size: ${widthMm === 58 ? '22px' : '25px'}; font-weight: 900; }
            .running-table { font-size: ${widthMm === 58 ? '18px' : '20px'}; font-weight: 900; text-transform: uppercase; }
            .kot-item { font-size: ${widthMm === 58 ? '18px' : '21px'}; font-weight: 900; padding-top: 4px; }
            .kot-qty { font-size: ${widthMm === 58 ? '18px' : '21px'}; font-weight: 700; text-align: right; padding-top: 4px; }
            .preview-toolbar {
              position: sticky;
              top: 0;
              z-index: 10;
              width: ${widthMm}mm;
              margin: 0 auto 8px;
              padding: 8px;
              background: #111827;
              color: #fff;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              font-family: Arial, sans-serif;
              font-size: 13px;
              box-shadow: 0 8px 22px rgba(15, 23, 42, 0.18);
            }
            .preview-toolbar-actions { display: flex; gap: 6px; }
            .preview-toolbar button {
              border: 0;
              border-radius: 6px;
              padding: 7px 10px;
              font-weight: 700;
              cursor: pointer;
            }
            .print-button { background: #4ce1b6; color: #06110e; }
            .close-button { background: #374151; color: #fff; }
            @media print {
              .preview-toolbar { display: none !important; }
              @page { size: ${widthMm}mm auto; margin: 0; }
              html, body {
                width: 100% !important;
                max-width: 100% !important;
                margin: 0 !important;
                padding: 1mm !important;
              }
              table, .receipt-row, .meta-grid {
                width: 100% !important;
                max-width: 100% !important;
              }
            }
          </style>
        </head>
        <body>
          ${showKotPreviewToolbar ? `
            <div class="preview-toolbar">
              <span>KOT Print Preview</span>
              <span class="preview-toolbar-actions">
                <button class="close-button" type="button" onclick="window.close()">Close</button>
                <button class="print-button" type="button" onclick="window.focus(); window.print()">Print KOT</button>
              </span>
            </div>
          ` : ''}
          ${printType === 'kot' ? `
            <div class="center kot-time">${escapeReceiptText(now.toLocaleDateString('en-GB'))} ${escapeReceiptText(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}</div>
            <div class="center kot-no">KOT - ${kotNumber}</div>
            <div class="center kot-type">${escapeReceiptText(orderTypeLabel)}</div>
            ${isRunningKot ? '<div class="center running-table">Running Table</div>' : ''}
            <div class="center kot-target">${escapeReceiptText(targetLabel || '—')}</div>
            <div class="line"></div>
            <table>
              <thead>
                <tr>
                  <th class="kot-item">Item</th>
                  <th class="kot-qty">Qt</th>
                </tr>
              </thead>
              <tbody>${kotItemRows}</tbody>
            </table>
            ${kotNotes ? `<div class="line"></div><div class="bold">Note</div><div>${escapeReceiptText(kotNotes)}</div>` : ''}
          ` : `
          ${hotel.hotelLogoUrl ? `<img class="logo" src="${escapeReceiptText(hotel.hotelLogoUrl)}" />` : ''}
          <div class="center title">${escapeReceiptText(hotel.hotelName)}</div>
          ${hotel.hotelAddress ? `<div class="center muted">${escapeReceiptText(hotel.hotelAddress)}</div>` : ''}
          <div class="center sub-title">${escapeReceiptText(title)}</div>
          ${hotel.hotelGstin && hasEnabledGst ? `<div class="center">GSTIN : ${escapeReceiptText(hotel.hotelGstin)}</div>` : ''}
          <div class="line"></div>
          ${customerName ? `<div class="receipt-row"><span>Name</span><span>${escapeReceiptText(customerName)}</span></div>` : ''}
          ${!customerName && printType !== 'kot' ? '<div>Name:</div><div class="name-line"></div>' : ''}
          ${customerPhone ? `<div class="receipt-row"><span>Phone</span><span>${escapeReceiptText(customerPhone)}</span></div>` : ''}
          <div class="line"></div>
          <div class="meta-grid">
            <div>Date: ${escapeReceiptText(now.toLocaleDateString('en-GB'))}</div>
            <div class="bold">${escapeReceiptText(targetLabel || orderTypeLabel)}</div>
            <div>${escapeReceiptText(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}</div>
            <div>Order No.: ${escapeReceiptText(orderNumber)}</div>
            <div>Cashier: ${escapeReceiptText(cashierName)}</div>
          </div>
          <div class="line"></div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th class="num">Qty</th>
                <th class="num">Rate</th>
                <th class="num">Amt</th>
              </tr>
            </thead>
            <tbody>${billItemRows}</tbody>
          </table>
          ${kotNotes ? `<div class="line"></div><div class="bold">Note</div><div>${escapeReceiptText(kotNotes)}</div>` : ''}
          <div class="line"></div>
          <div class="receipt-row"><span>Total Qty</span><span>${formatReceiptAmount(orderItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0)).replace('.00', '')}</span></div>
          <div class="receipt-row"><span>Sub Total</span><span>${formatReceiptAmount(subtotal)}</span></div>
          ${receiptDiscountAmount > 0 ? `<div class="receipt-row"><span>Discount</span><span>-${formatReceiptAmount(receiptDiscountAmount)}</span></div>` : ''}
          ${deliveryCharge > 0 ? `<div class="receipt-row"><span>Delivery</span><span>${formatReceiptAmount(deliveryCharge)}</span></div>` : ''}
          ${containerCharge > 0 ? `<div class="receipt-row"><span>Container</span><span>${formatReceiptAmount(containerCharge)}</span></div>` : ''}
          ${taxLines}
          ${Math.abs(roundOffAmount) > 0.004 ? `<div class="receipt-row"><span>Round off</span><span>${roundOffAmount >= 0 ? '' : '-'}${formatReceiptAmount(Math.abs(roundOffAmount))}</span></div>` : ''}
          <div class="line"></div>
          <div class="receipt-row grand"><span>Grand Total</span><span>₹ ${formatReceiptAmount(displayGrandTotal)}</span></div>
          `}
          <div class="line"></div>
          <div class="center">Thanks</div>
          ${desktopShell ? '' : `
          <script>
            ${!autoPrintOnLoad ? `
              window.onload = function () {
                window.focus();
              };
            ` : `
              window.onload = function () {
                setTimeout(function () {
                  window.focus();
                  window.print();
                }, 150);
              };
            `}
          </script>`}
        </body>
      </html>`;
}

/** Web path: same hidden-iframe + window.print() used by billing today. */
export function printReceiptHtmlDirectly(html, title = 'Restaurant Print') {
  if (!html) return false;
  const printFrame = document.createElement('iframe');
  printFrame.title = title;
  printFrame.style.position = 'fixed';
  printFrame.style.right = '0';
  printFrame.style.bottom = '0';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.style.pointerEvents = 'none';
  document.body.appendChild(printFrame);

  const cleanup = () => {
    setTimeout(() => {
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    }, 500);
  };

  try {
    const frameDocument = printFrame.contentDocument || printFrame.contentWindow?.document;
    if (!frameDocument || !printFrame.contentWindow) {
      cleanup();
      return false;
    }
    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();
    setTimeout(() => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } finally {
        cleanup();
      }
    }, 250);
    return true;
  } catch (_err) {
    cleanup();
    return false;
  }
}

/**
 * Print via existing desktop ESC/POS / web iframe paths (unchanged engines).
 */
export async function printRestaurantReceiptHtml(html, title = 'Restaurant Print') {
  if (hasDesktopPrintApi()) {
    if (!isPrinterValidated()) {
      openPrinterValidationDialog();
      return { opened: false, reason: 'printer_not_validated' };
    }
    const printKind = title.startsWith('KOT') ? 'kot' : 'bill';
    const result = await silentPrintRestaurantHtml(html, { printKind });
    if (!result.success) {
      return { opened: false, reason: 'print_failed', error: result.error };
    }
    return { opened: true, silent: true };
  }

  const didPrint = printReceiptHtmlDirectly(html, title);
  return didPrint ? { opened: true, silent: false } : { opened: false, reason: 'blocked' };
}

export function downloadRestaurantReceiptHtml(html, filename = 'invoice.html') {
  if (!html) return false;
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (_err) {
    return false;
  }
}
